/**
 * Drawing and HTML Processing Utilities
 *
 * This file contains utility functions for drawing restrictions and HTML content processing
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

import {
  getCanvasStageHeightPx,
  getMainCanvasMaxRadiusPx,
  getMainCanvasWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "./mainCanvasLayout";

/**
 * Apply drawing restrictions to shape properties
 * @param {string} shapeType - Type of shape (rectangle, circle, arrow, line)
 * @param {Object} properties - Shape properties to restrict
 * @param {string} canvasHeight - Canvas height for calculations
 * @param {number} [viewportWidth] - Editor viewport width (e.g. canvasViewportWidth) for main-stage pixel width
 * @returns {Object} Restricted properties
 */
export const createApplyDrawingRestrictions =
  (canvasHeight, viewportWidth) => (shapeType, properties) => {
    const mainW = getMainCanvasWidthPx(viewportWidth);
    // Keep shared drawing restrictions from shrinking off-canvas vertical range
    // when main canvas height % is reduced.
    const stageH = Math.max(
      getStageHeightPxFromCanvasHeight(canvasHeight),
      getCanvasStageHeightPx(),
    );
    const maxR = getMainCanvasMaxRadiusPx(viewportWidth, stageH);
    const maxContentH = Math.min(600, Math.max(1, stageH));

    const restrictions = {
      rectangle: {
        MIN_WIDTH: 1,
        MAX_WIDTH: mainW,
        MIN_HEIGHT: 1,
        MAX_HEIGHT: maxContentH,
        // Remove position restrictions for rectangles to allow creation at cursor position
        MIN_X: 0,
        MAX_X: window.innerWidth,
        MIN_Y: 0,
        MAX_Y: stageH,
      },
      circle: {
        MIN_RADIUS: 5,
        MAX_RADIUS: maxR,
        // Remove position restrictions for circles to allow creation at cursor position
        MIN_X: 0,
        MAX_X: window.innerWidth,
        MIN_Y: 0,
        MAX_Y: stageH,
      },
      arrow: {
        MIN_WIDTH: 30,
        MAX_WIDTH: mainW,
        MIN_HEIGHT: 1,
        MAX_HEIGHT: maxContentH,
        MIN_X: 0,
        MAX_X: window.innerWidth,
        MIN_Y: 0,
        MAX_Y: stageH,
      },
      line: {
        MIN_WIDTH: 30,
        MAX_WIDTH: mainW,
        MIN_HEIGHT: 1,
        MAX_HEIGHT: maxContentH,
        MIN_X: 0,
        MAX_X: window.innerWidth,
        MIN_Y: 0,
        MAX_Y: stageH,
      },
      text: {
        MIN_WIDTH: 10,
        MAX_WIDTH: mainW,
        MIN_HEIGHT: 100, // Fixed height, user can't change during drawing
        MAX_HEIGHT: 100,
        MIN_X: 0,
        MAX_X: window.innerWidth,
        MIN_Y: 0,
        MAX_Y: stageH,
      },
      image: {
        MIN_WIDTH: 10,
        MAX_WIDTH: mainW,
        MIN_HEIGHT: 10,
        MAX_HEIGHT: maxContentH,
        // Remove position restrictions for images to allow creation at cursor position
        MIN_X: 0,
        MAX_X: window.innerWidth,
        MIN_Y: 0,
        MAX_Y: stageH,
      },
    };

    const shapeRestrictions = restrictions[shapeType];
    if (!shapeRestrictions) return properties;

    const restricted = { ...properties };

    // Apply position restrictions (skip for circles, rectangles, text, and images to allow creation at cursor position)
    if (shapeType !== "circle" && shapeType !== "rectangle" && shapeType !== "text" && shapeType !== "image") {
      if (restricted.x !== undefined) {
        restricted.x = Math.max(
          shapeRestrictions.MIN_X,
          Math.min(shapeRestrictions.MAX_X, restricted.x)
        );
      }
      if (restricted.y !== undefined) {
        restricted.y = Math.max(
          shapeRestrictions.MIN_Y,
          Math.min(shapeRestrictions.MAX_Y, restricted.y)
        );
      }
    }

    // Apply size restrictions
    if (restricted.width !== undefined) {
      restricted.width = Math.max(
        shapeRestrictions.MIN_WIDTH,
        Math.min(shapeRestrictions.MAX_WIDTH, restricted.width)
      );
    }
    if (restricted.height !== undefined) {
      restricted.height = Math.max(
        shapeRestrictions.MIN_HEIGHT,
        Math.min(shapeRestrictions.MAX_HEIGHT, restricted.height)
      );
    }
    if (restricted.radius !== undefined) {
      restricted.radius = Math.max(
        shapeRestrictions.MIN_RADIUS,
        Math.min(shapeRestrictions.MAX_RADIUS, restricted.radius)
      );
    }
    if (restricted.radiusX !== undefined) {
      restricted.radiusX = Math.max(
        shapeRestrictions.MIN_RADIUS,
        Math.min(shapeRestrictions.MAX_RADIUS, restricted.radiusX)
      );
    }
    if (restricted.radiusY !== undefined) {
      restricted.radiusY = Math.max(
        shapeRestrictions.MIN_RADIUS,
        Math.min(shapeRestrictions.MAX_RADIUS, restricted.radiusY)
      );
    }

    return restricted;
  };

/**
 * Center-anchored image: keep center inside stage so the full bitmap fits (matches pointerHandlers).
 */
export function clampImageCenterToStageBounds(
  centerX,
  centerY,
  width,
  height,
  stageWidth,
  stageHeight,
  border = 0,
) {
  const halfW = Math.max(0, Number(width) || 0) / 2 + border;
  const halfH = Math.max(0, Number(height) || 0) / 2 + border;
  const sw = Number(stageWidth) || 0;
  const sh = Number(stageHeight) || 0;
  if (sw <= 0 || sh <= 0) {
    return { x: centerX, y: centerY };
  }
  return {
    x: Math.max(halfW, Math.min(Number(centerX) || 0, sw - halfW)),
    y: Math.max(halfH, Math.min(Number(centerY) || 0, sh - halfH)),
  };
}

/**
 * Clamp rectangle top-left x/y so the rect (including stroke) stays inside the stage.
 * Matches RectangleElement, which always treats x/y as top-left.
 */
export function clampRectangleTopLeftToStageBounds(
  rect,
  stageWidth,
  stageHeight,
  strokeWidth = 2,
  edgeInsetPx = 0,
) {
  if (
    !rect ||
    !Number.isFinite(stageWidth) ||
    !Number.isFinite(stageHeight) ||
    stageWidth <= 0 ||
    stageHeight <= 0
  ) {
    return rect;
  }
  const strokeHalf = Math.max(0, Number(strokeWidth) || 0) / 2;
  const safeWidth = Math.max(0, Number(rect.width) || 0);
  const safeHeight = Math.max(0, Number(rect.height) || 0);
  const maxInsetX = Math.max(0, (stageWidth - safeWidth - strokeHalf * 2) / 2);
  const maxInsetY = Math.max(0, (stageHeight - safeHeight - strokeHalf * 2) / 2);
  const edgeInsetX = Math.min(Math.max(0, Number(edgeInsetPx) || 0), maxInsetX);
  const edgeInsetY = Math.min(Math.max(0, Number(edgeInsetPx) || 0), maxInsetY);
  const minX = strokeHalf + edgeInsetX;
  const minY = strokeHalf + edgeInsetY;
  const maxX = Math.max(minX, stageWidth - safeWidth - strokeHalf - edgeInsetX);
  const maxY = Math.max(minY, stageHeight - safeHeight - strokeHalf - edgeInsetY);
  return {
    ...rect,
    x: Math.max(minX, Math.min(Number(rect.x) || 0, maxX)),
    y: Math.max(minY, Math.min(Number(rect.y) || 0, maxY)),
  };
}

/**
 * Clamp circle center so the circle (including stroke) stays inside the stage.
 */
export function clampCircleCenterToStageBounds(
  circle,
  stageWidth,
  stageHeight,
  strokeWidth = 2,
  edgeInsetPx = 0,
) {
  if (
    !circle ||
    !Number.isFinite(stageWidth) ||
    !Number.isFinite(stageHeight) ||
    stageWidth <= 0 ||
    stageHeight <= 0
  ) {
    return circle;
  }
  const safeRadius = Math.max(0, Number(circle.radius) || 0);
  const strokeHalf = Math.max(0, Number(strokeWidth) || 0) / 2;
  const maxInsetX = Math.max(0, (stageWidth - safeRadius * 2 - strokeHalf * 2) / 2);
  const maxInsetY = Math.max(0, (stageHeight - safeRadius * 2 - strokeHalf * 2) / 2);
  const edgeInsetX = Math.min(Math.max(0, Number(edgeInsetPx) || 0), maxInsetX);
  const edgeInsetY = Math.min(Math.max(0, Number(edgeInsetPx) || 0), maxInsetY);
  const minX = safeRadius + strokeHalf + edgeInsetX;
  const minY = safeRadius + strokeHalf + edgeInsetY;
  const maxX = Math.max(minX, stageWidth - safeRadius - strokeHalf - edgeInsetX);
  const maxY = Math.max(minY, stageHeight - safeRadius - strokeHalf - edgeInsetY);
  return {
    ...circle,
    x: Math.max(minX, Math.min(Number(circle.x) || 0, maxX)),
    y: Math.max(minY, Math.min(Number(circle.y) || 0, maxY)),
  };
}

/**
 * Shift unified line/arrow group (x, y + relative points) so the stroke bbox stays inside the stage.
 * Used for UnifiedLineArrowElement / lines with a points array.
 */
export function clampPolylineGroupToStageBounds(
  item,
  stageWidth,
  stageHeight,
  strokeWidth = 2,
  edgeInsetPx = 0,
) {
  if (
    !item ||
    !Array.isArray(item.points) ||
    item.points.length < 4 ||
    !Number.isFinite(stageWidth) ||
    !Number.isFinite(stageHeight) ||
    stageWidth <= 0 ||
    stageHeight <= 0
  ) {
    return item;
  }
  const strokeHalf = Math.max(0, Number(strokeWidth) || 0) / 2;
  const edgeInset = Math.max(0, Number(edgeInsetPx) || 0);
  const edgePad = strokeHalf + edgeInset;
  let baseX = Number(item.x) || 0;
  let baseY = Number(item.y) || 0;
  const pts = item.points;

  const measure = (bx, by) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < pts.length; i += 2) {
      const px = bx + (Number(pts[i]) || 0);
      const py = by + (Number(pts[i + 1]) || 0);
      minX = Math.min(minX, px);
      maxX = Math.max(maxX, px);
      minY = Math.min(minY, py);
      maxY = Math.max(maxY, py);
    }
    return { minX, maxX, minY, maxY };
  };

  for (let pass = 0; pass < 6; pass += 1) {
    const { minX, maxX, minY, maxY } = measure(baseX, baseY);
    let dx = 0;
    let dy = 0;
    if (minX - edgePad < 0) dx = -(minX - edgePad);
    else if (maxX + edgePad > stageWidth) dx = stageWidth - (maxX + edgePad);
    if (minY - edgePad < 0) dy = -(minY - edgePad);
    else if (maxY + edgePad > stageHeight) dy = stageHeight - (maxY + edgePad);
    if (dx === 0 && dy === 0) break;
    baseX += dx;
    baseY += dy;
  }

  return { ...item, x: baseX, y: baseY };
}

/**
 * Process HTML content to plain text
 * @param {string} html - HTML content to process
 * @returns {string} Processed plain text
 */
export const createProcessHtmlContent = () => (html) => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Process lists and other formatting
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();

      if (tagName === "ul") {
        const items = Array.from(node.querySelectorAll("li"))
          .map((li) => `• ${li.textContent.trim()}`)
          .join("\n");
        return "\n" + items + "\n";
      }

      if (tagName === "ol") {
        const items = Array.from(node.querySelectorAll("li"))
          .map((li, index) => ` ${index + 1}. ${li.textContent.trim()}`)
          .join("\n");
        return "\n" + items + "\n";
      }

      if (tagName === "li") {
        return node.textContent;
      }

      if (tagName === "br") {
        return "\n";
      }

      if (tagName === "div") {
        const children = Array.from(node.childNodes).map(processNode).join("");
        return children + "\n";
      }

      if (tagName === "span") {
        return Array.from(node.childNodes).map(processNode).join("");
      }

      if (tagName === "a") {
        return node.textContent;
      }

      // For other elements, process their children
      return Array.from(node.childNodes).map(processNode).join("");
    }

    return "";
  };

  return processNode(tempDiv).trim();
};

/**
 * Process HTML content with formatting for SVG export
 * @param {string} html - HTML content to process
 * @param {number} startY - Starting Y position
 * @param {number} fontSizeParam - Font size parameter
 * @returns {Object} Object with content and height
 */
export const createProcessHtmlContentWithFormatting =
  () =>
  (html, startY, fontSizeParam = 14) => {
    if (!html) return { content: "", height: 0 };
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const fontSize = fontSizeParam;
    const lineHeight = fontSize * 1.4;
    const leftPadding = 15;
    let tspans = [];
    let lineCount = 0;

    function renderNode(node, style = {}) {
      const effectiveStyle = {
        color: style.color || "black",
        fontWeight: style.fontWeight || "normal",
        fontStyle: style.fontStyle || "normal",
      };
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.trim()) {
          tspans.push(
            `<tspan x="${leftPadding}" dy="${
              lineCount === 0 ? 0 : lineHeight
            }" fill="${effectiveStyle.color}" font-weight="${
              effectiveStyle.fontWeight
            }" font-style="${effectiveStyle.fontStyle}">${
              node.textContent
            }</tspan>`
          );
          lineCount++;
        }
        return;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        let newStyle = { ...effectiveStyle };
        const tag = node.tagName.toLowerCase();
        if (tag === "span" && node.style.color) {
          newStyle.color = node.style.color;
        }
        if (tag === "b" || tag === "strong") {
          newStyle.fontWeight = "bold";
        }
        if (tag === "i" || tag === "em") {
          newStyle.fontStyle = "italic";
        }
        if (tag === "br" || tag === "p" || tag === "div") {
          // New line for <br>, <p>, <div>
          if (lineCount > 0) {
            tspans.push(
              `<tspan x="${leftPadding}" dy="${lineHeight}"></tspan>`
            );
            lineCount++;
          }
        }
        Array.from(node.childNodes).forEach((child) =>
          renderNode(child, newStyle)
        );
      }
    }

    Array.from(tempDiv.childNodes).forEach((child) => renderNode(child));
    if (tspans.length === 0) return { content: "", height: 0 };

    // Wrap all tspans in a single <text>
    const textBlock = `<text x="${leftPadding}" y="${startY}" font-size="${fontSize}" font-family="Roboto">${tspans.join(
      ""
    )}</text>`;
    return { content: textBlock, height: lineCount * lineHeight };
  };
